import React, { useCallback, useEffect, useState } from 'react';
import { Empty, Modal, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { crmFlowResponses } from '../../api/Api';
import Loader from '../../../../components/commonComponents/Loader/Index';
import { cn } from '../../../../helper/cn';

/**
 * Helper function to convert snake_case or space-separated strings to Title Case
 * @param {string} value - The string to convert
 * @returns {string} Title cased string
 */
const toTitleCase = (value = '') => {
  const cleaned = value.replace(/[_\s]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Capitalizes the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} String with first letter capitalized
 */
const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats flow answer values by removing numeric prefixes and timestamps
 * @param {*} value - The answer value to format
 * @returns {string|null} Formatted answer or null
 */
const formatFlowAnswer = (value) => {
  if (value === null || value === undefined) return null;
  const stringified = typeof value === 'string' ? value : String(value);
  // Remove numeric prefixes such as "0_answer" and trailing timestamps
  const withoutPrefix = stringified.replace(/^\d+_/, '');
  const withoutTimestamp = withoutPrefix.replace(/\s+\d{10,}$/, '').trim();
  return toTitleCase(withoutTimestamp);
};

/**
 * Parses flow responses from answers object and sorts by screen index
 * @param {Object} answers - The answers object from flow response
 * @returns {Array} Sorted array of parsed flow responses with question labels and answers
 */
const parseFlowResponses = (answers) => {
  if (!answers) return [];

  const entries = Object.entries(answers)
    .filter(([key]) => key !== 'flow_token')
    .map(([key, value]) => {
      let screenIndex = Number.MAX_SAFE_INTEGER;
      let labelSection = key;

      // Case 1: "screen_2_question_name"
      const screenMatch = key.match(/^screen_(\d+)_/);
      if (screenMatch) {
        screenIndex = Number(screenMatch[1]);
        labelSection = key.slice(screenMatch[0].length);
      } else {
        // Case 2: "0_question_name" → treat leading number as screen index
        const numericPrefixMatch = key.match(/^(\d+)_/);
        if (numericPrefixMatch) {
          screenIndex = Number(numericPrefixMatch[1]);
          labelSection = key.slice(numericPrefixMatch[0].length);
        }
      }

      const labelParts = labelSection.split('_');
      const maybeIndex = labelParts[labelParts.length - 1];
      const questionLabelParts = /^\d+$/.test(maybeIndex)
        ? labelParts.slice(0, -1)
        : labelParts;
      const rawLabel = questionLabelParts.join(' ');
      const questionLabel = toTitleCase(
        rawLabel.replace(/\s+\d{10,}$/, '').trim(),
      );

      const answersArray = Array.isArray(value) ? value : [value];
      const answersList = answersArray
        .map((answer) => formatFlowAnswer(answer))
        .filter(Boolean);

      return {
        screenIndex,
        questionLabel,
        answers: answersList,
      };
    })
    .filter((entry) => entry.answers.length);

  return entries.sort((a, b) => a.screenIndex - b.screenIndex);
};

/**
 * Simplified modal component with clean green theme and QUESTION/ANSWER labels
 * Displays flow responses in a numbered list format
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {Array} responses - Parsed flow responses to display
 */
const FlowResponseModal = ({ open, onClose, responses }) => (
  <Modal
    open={open}
    footer={null}
    centered
    title="Flow Responses"
    onCancel={onClose}
    destroyOnClose
    className="flow-response-modal-clean !p-2"
    width={550}
  >
    <div className="max-h-[65vh] overflow-y-auto py-2">
      {responses.length ? (
        <div className="space-y-3">
          {responses.map((item, index) => (
            <div
              key={`${item.screenIndex}-${item.questionLabel}-${index}`}
              className="rounded-lg border border-gray-200 bg-white p-4 hover:border-green-300 transition-colors"
            >
              {/* Question number and label */}
              <div className="flex items-start gap-3">
                {/* Simple numbered circle with green color */}
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-green-700 text-sm font-semibold">{index + 1}</span>
                </div>

                <div className="flex-1">
                  {/* QUESTION label */}
                  {item.questionLabel && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                        QUESTION
                      </p>
                      <p className="text-sm font-medium text-gray-900 m-0">
                        {item.questionLabel}
                      </p>
                    </div>
                  )}

                  {/* ANSWER label and content */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      {item.answers.length > 1 ? 'ANSWERS' : 'ANSWER'}
                    </p>
                    <div className="space-y-2">
                      {item.answers.map((answer, idx) => (
                        <div
                          key={idx}
                          className="text-sm text-gray-700 py-2 px-3 bg-gray-50 rounded"
                        >
                          {answer}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="inline-block p-4 bg-gray-100 rounded-full mb-3">
            <EyeOutlined className="text-2xl text-gray-400" />
          </div>
          <p className="text-sm text-gray-600">No flow responses available.</p>
        </div>
      )}
    </div>
  </Modal>
);

/**
 * Main FlowResponses component - displays a table of flow responses for a contact
 * Columns: Flow Name | Broadcast Name | Sequence Name | Submitted At | Actions
 * Each row is clickable to open the response modal
 * @param {string} contactPersonId - The contact person ID to fetch responses for
 * @param {string} brandId - The brand ID
 */
const FlowResponses = ({ contactPersonId, brandId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedResponses, setSelectedResponses] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Fetches flow responses from the API with pagination
   * @param {number} requestedPage - The page number to fetch
   */
  const fetchResponses = useCallback(
    async (requestedPage = 1) => {
      if (!contactPersonId || !brandId) return;

      if (requestedPage === 1) setLoading(true);

      try {
        const res = await crmFlowResponses(
          brandId,
          contactPersonId,
          requestedPage,
          20,
        );
        if (res && res.data && res.data.data) {
          const newItems = res.data.data;
          const pagination = res.data.pagination || {};

          setItems((prev) =>
            requestedPage === 1 ? newItems : [...prev, ...newItems],
          );
          setHasMore(requestedPage < (pagination.total_pages || 1));
          setPage(requestedPage + 1);
        }
      } catch (err) {
        console.error('Failed to fetch flow responses:', err);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [contactPersonId, brandId],
  );

  // Initial fetch on component mount or when dependencies change
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    fetchResponses(1);
  }, [contactPersonId, brandId, fetchResponses]);

  /**
   * Opens the modal with parsed responses
   * @param {Object} answers - The answers object to parse and display
   * @param {Event} e - Click event (to stop propagation if needed)
   */
  const handleViewResponses = (answers, e) => {
    // Stop propagation only if clicking the button (not the row)
    if (e && e.target.closest('button')) {
      e.stopPropagation();
    }
    const parsed = parseFlowResponses(answers);
    setSelectedResponses(parsed);
    setIsModalOpen(true);
  };

  /**
   * Handles row click to open modal
   * @param {Object} item - The flow response item
   */
  const handleRowClick = (item) => {
    if (item.answers && Object.keys(item.answers).length > 0) {
      handleViewResponses(item.answers);
    }
  };

  // CSS classes for table styling (matching campaign execution table and flow insights)
  const headerStyle = "text-sm font-semibold text-[var(--font-600)] text-left";
  const bodyStyle = "text-sm font-medium text-[var(--font-600)] text-left";

  return (
    <div className="flex flex-col h-full bg-white rounded-lg overflow-hidden">
      {/* Header section */}
      <div className="px-[20px] py-[15px] border-b border-gray-100">
        <h3 className="font-[600] text-black text-[1vw] m-0">Flow Responses</h3>
        <p className="text-xs text-gray-500 mt-1">View all flow submissions from this contact</p>
      </div>

      {/* Table content with infinite scroll */}
      <div className="flex-1 overflow-hidden">
        {/* Show loading state for initial load */}
        {loading && items.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <Loader Width={30} Height={30} />
          </div>
        ) : items.length > 0 ? (
          <>
            {/* Table header - sticky */}
            <div className="w-full h-14 sticky top-0 flex items-center pr-1 bg-white border-b border-gray-200 px-[20px]">
              <div className={cn(headerStyle, "w-[25%] pl-4")}>Flow Name</div>
              <div className={cn(headerStyle, "w-[20%]")}>Broadcast Name</div>
              <div className={cn(headerStyle, "w-[20%]")}>Sequence Name</div>
              <div className={cn(headerStyle, "w-[20%]")}>Submitted At</div>
              <div className={cn(headerStyle, "w-[15%] text-center")}>Actions</div>
            </div>

            {/* Scrollable content */}
            <div
              id="flow-responses-scrollable"
              className="overflow-auto px-[20px]"
              style={{ height: 'calc(107vh - 35vh)' }}
            >
              <InfiniteScroll
                dataLength={items.length}
                next={() => fetchResponses(page)}
                hasMore={hasMore}
                loader={
                  <div className="py-4 text-center flex justify-center">
                    <Loader Width={20} Height={20} />
                  </div>
                }
                scrollableTarget="flow-responses-scrollable"
              >
                {items.map((item, index) => {
                  const date = item.completed_at ? dayjs(item.completed_at) : null;
                  // Check if item has answers to show - determines if row should be clickable
                  const hasAnswers = item.answers && Object.keys(item.answers).length > 0;

                  return (
                    <div
                      key={item.id || `response-${index}`}
                      className={`w-full odd:bg-gray-50 my-1 py-4 flex items-center rounded-lg hover:bg-green-50 transition-colors duration-200 group ${
                        hasAnswers ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => hasAnswers && handleRowClick(item)}
                    >
                      {/* Flow Name - capitalized first letter with left padding */}
                      <div className={cn(bodyStyle, "w-[25%] pl-4")}>
                        <span className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                          {capitalizeFirstLetter(item.flow_name) || 'Unnamed Flow'}
                        </span>
                      </div>

                      {/* Broadcast Name column (separate from sequence) */}
                      <div className={cn(bodyStyle, "w-[20%]")}>
                        {item.broadcast_name ? (
                          <span className="capitalize text-sm text-gray-700">
                            {item.broadcast_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </div>

                      {/* Sequence Name column (separate from broadcast) */}
                      <div className={cn(bodyStyle, "w-[20%]")}>
                        {item.campaign_sequence_name ? (
                          <span className="capitalize text-sm text-gray-700">
                            {item.campaign_sequence_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </div>

                      {/* Submitted At */}
                      <div className={cn(bodyStyle, "w-[20%]")}>
                        {date ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm text-gray-700">
                              {date.format('MMM DD, YYYY')}
                            </span>
                            <span className="text-xs text-gray-500">
                              {date.format('hh:mm A')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </div>

                      {/* View Responses Button with green color */}
                      <div className={cn(bodyStyle, "w-[15%] flex justify-center")}>
                        {hasAnswers ? (
                          <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            className="!bg-[#00A85A] hover:!bg-[#018854] !text-white shadow-sm hover:shadow-md transition-all"
                            onClick={(e) => handleViewResponses(item.answers, e)}
                          >
                            View
                          </Button>
                        ) : (
                          <span className="text-gray-400 text-xs">No data</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </InfiniteScroll>
            </div>
          </>
        ) : (
          // Empty state
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
              <EyeOutlined className="text-4xl text-gray-400" />
            </div>
            <h4 className="text-lg font-semibold text-gray-700 mb-2">No Flow Responses Yet</h4>
            <p className="text-sm text-gray-500 text-center max-w-md">
              This contact hasn't submitted any flow responses. Flow responses will appear here once the contact completes a flow.
            </p>
          </div>
        )}
      </div>

      {/* Response Modal */}
      <FlowResponseModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        responses={selectedResponses || []}
      />

      {/* Clean modal styles with subtle green accents */}
      <style jsx global>{`
        .flow-response-modal-clean .ant-modal-content {
          border-radius: 12px;
          padding: 0 !important;
        }
        .flow-response-modal-clean .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          padding: 16px 24px;
        }
        .flow-response-modal-clean .ant-modal-body {
          padding: 16px 24px 24px 24px;
        }
        .flow-response-modal-clean .ant-modal-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }
      `}</style>
    </div>
  );
};

export default FlowResponses;
